'use client';

/**
 * LayoutEngine — generic card-placement renderer (canvas surface).
 *
 * Reads a list of `CardPlacement` records and paints them onto a
 * 12-column CSS Grid. `(x, y, w, h)` are pure data; the engine
 * doesn't branch on layout shape — vertical stack, 2×2 grid, or
 * sparse positioning are all just placements with different
 * coordinates.
 *
 * `sticky` pins a placement to the viewport during scroll (e.g.
 * Next Move card, KPI strip). `scrollable` makes a placement an
 * independently-scrolling region.
 *
 * Phase 1 of the cards-everywhere prototype: validates the
 * primitive on real data with a single placement (one card,
 * full-width). The same engine renders 2×2, 4×3, sparse, etc. once
 * the matrix declares them — no code change.
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import type { CardPlacement } from '@/lib/api/types';
import { cn } from '@/lib/utils/cn';
import { CANVAS_CARD_REGISTRY } from './card-registry';
import { CardChromeProvider } from '@/lib/design/card-chrome';
import { CardPlacementProvider, type CardSurface } from '@/lib/design/card-export';
import { CardErrorBoundary } from './card-error-boundary';
import { CanvasCardShell } from './canvas-card-shell';
import { useIsMobile } from '@/lib/hooks/use-is-mobile';

interface LayoutEngineProps {
  placements: CardPlacement[];
  cols?: number;
  /** Render as a vertical SCROLL-stack instead of the viewport-fit grid —
   *  each card gets a definite min-height from its `h` (so Recharts cards
   *  resolve and don't collapse), and the column scrolls past the viewport.
   *  This is the desktop form of the mobile path: the right shape for a
   *  variable-length answer (Ask overlay) where a chart wants real height
   *  rather than a viewport fraction. The grid stays the default for
   *  designed compositions (matrix / PDP) that share one screen. */
  stack?: boolean;
  /** Which scene surface these placements mount on — threaded into each
   *  card's CardPlacementProvider so the capture envelope files it under
   *  the right key. Defaults to 'canvas' (the main pane); the agent-canvas
   *  overlay passes 'overlay' so a card that flips rail→overlay doesn't
   *  vanish from (or mislabel itself in) scene captures. */
  surface?: CardSurface;
}

export function LayoutEngine({ placements, cols = 12, stack = false, surface = 'canvas' }: LayoutEngineProps) {
  const { isMobile } = useIsMobile();

  if (placements.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
        No cards configured for this selection
      </div>
    );
  }

  // Single full-bleed placement (one card spanning the canvas) bypasses
  // the grid container — the card owns the viewport directly. Avoids
  // `gridAutoRows: auto` collapsing kanban / timeline / graph views
  // that expect to fill their parent.
  const isFullBleed =
    placements.length === 1 &&
    (placements[0].w ?? 1) >= cols &&
    (placements[0].h ?? 1) >= cols;

  if (!stack && isFullBleed) {
    return <FullBleedSlot placement={placements[0]} surface={surface} />;
  }

  // Scroll-stack — explicit (`stack`, e.g. the Ask overlay) or mobile (where
  // the grid never fits). Each card gets a definite min-height from its `h`,
  // and the column scrolls; a chart sharing the surface with a compact card
  // gets the room it needs instead of a viewport fraction.
  if (stack || isMobile) {
    return (
      <div className={cn('h-full overflow-y-auto flex flex-col', surface === 'canvas' ? 'p-5 gap-6 bg-background' : 'p-3 gap-3')}>
        {placements.map((p) => (
          <StackSlot key={p.slot_id} placement={p} surface={surface} />
        ))}
      </div>
    );
  }

  // Per-row sizing — `auto` for short rows (only h=1 placements cover
  // them), `1fr` for any row covered by an h>1 placement. The fr rows
  // share the remaining vertical space; auto rows take only as much
  // height as their content. This handles the "KPI strip + big paint
  // below" pattern (KPI gets auto, the tall placement fills the rest)
  // while the multi-card 2×2 / 4×3 patterns work the same as before
  // (every row is 1fr → equal heights).
  const rowTemplate = buildRowTemplate(placements);

  // Debug aid — append `?grid` to the URL to overlay the column guides so
  // x/w placement coords can be read off the screen. Opt-in, no-op
  // otherwise; renders behind the cards, pointer-events-none.
  const showGrid =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('grid');

  // The wrapper scrolls vertically: a layout whose content-sized (h=1) rows
  // outgrow the screen scrolls the canvas instead of clipping it. A layout
  // that fits (the usual 1fr rows) never overflows, so no scrollbar shows.
  return (
    <div className="relative h-full overflow-y-auto overflow-x-hidden">
      {showGrid && (
        <div
          className="absolute inset-0 p-3 pointer-events-none z-0"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gap: '0.75rem',
          }}
        >
          {Array.from({ length: cols }, (_, i) => (
            <div key={i} className="relative border-l border-dashed border-primary/30">
              <span className="absolute top-0 left-0.5 text-[9px] font-mono leading-none text-primary/50">
                {i}
              </span>
            </div>
          ))}
          {/* right edge of the last column */}
          <div className="absolute right-3 top-3 bottom-3 border-r border-dashed border-primary/30" />
        </div>
      )}
      <div
        className="h-full p-3 relative z-10"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: rowTemplate,
          // Rows pack to the top. With a 1fr row the fr row takes the spare height anyway; without one (every
          // placement h=1, content-sized) the default stretch would pad each row to fill the screen.
          alignContent: 'start',
          gap: '0.75rem',
        }}
      >
        {placements.map((p) => (
          <PlacementSlot key={p.slot_id} placement={p} cols={cols} surface={surface} />
        ))}
      </div>
    </div>
  );
}

function buildRowTemplate(placements: CardPlacement[]): string {
  if (placements.length === 0) return '';
  const maxY = Math.max(
    ...placements.map((p) => (p.y ?? 0) + (p.h ?? 1)),
  );
  const rows: string[] = [];
  for (let i = 0; i < maxY; i++) {
    const anyTall = placements.some((p) => {
      const y = p.y ?? 0;
      const h = p.h ?? 1;
      return y <= i && i < y + h && h > 1;
    });
    rows.push(anyTall ? 'minmax(0, 1fr)' : 'auto');
  }
  return rows.join(' ');
}

function FullBleedSlot({ placement, surface }: { placement: CardPlacement; surface: CardSurface }) {
  const Component = CANVAS_CARD_REGISTRY[placement.content_key];
  if (!Component) {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        `[LayoutEngine] Unknown content_key '${placement.content_key}' — add to CANVAS_CARD_REGISTRY`,
      );
    }
    return null;
  }
  // Surface tagging is the renderer's job, not the card's — same as
  // PlacementSlot. Without this provider a full-bleed card (graph,
  // kanban, timeline, matrix…) registers with no `surface`, so the
  // capture envelope scatters it into `scene.surfaces.unknown` instead
  // of `canvas`. The geometry is the whole canvas by construction.
  const w = placement.w ?? 1;
  const x = placement.x ?? 0;
  const y = placement.y ?? 0;
  const h = placement.h ?? 1;

  return (
    <CanvasCardShell contentKey={placement.content_key} className="h-full w-full overflow-hidden">
      <CardErrorBoundary contentKey={placement.content_key}>
        <CardChromeProvider
          contentKey={placement.content_key}
          bundle={placement.typography ?? null}
          label={placement.label ?? null}
          iconName={placement.icon ?? null}
          strategy={placement.strategy ?? null}
        >
          <CardPlacementProvider
            value={{
              surface,
              slot_id: placement.slot_id,
              placement: { x, y, w, h },
            }}
          >
            <Component {...(placement.props ?? {})} />
          </CardPlacementProvider>
        </CardChromeProvider>
      </CardErrorBoundary>
    </CanvasCardShell>
  );
}

function StackSlot({ placement, surface }: { placement: CardPlacement; surface: CardSurface }) {
  const Component = CANVAS_CARD_REGISTRY[placement.content_key];
  if (!Component) return null;

  // Same surface-tagging duty as PlacementSlot / FullBleedSlot — the
  // scroll-stack is still the canvas surface; without the provider its
  // cards would land in `scene.surfaces.unknown`.
  const w = placement.w ?? 1;
  const x = placement.x ?? 0;
  const y = placement.y ?? 0;
  const h = placement.h ?? 1;

  // `h` is a height intent for FILL-CONTAINER cards (Recharts
  // `height:100%` collapses without a definite box) — they keep a
  // min-height floor. `scrollable` marks PROSE slots: content-height,
  // no reserved window at all — the mock's rule (cards fit their
  // content; the overlay COLUMN scrolls). `empty:hidden` collapses a
  // slot whose card rendered null (e.g. no narration lane).
  const minHeight = `${Math.max(h, 4) * 3}rem`;

  return (
    <CanvasCardShell
      contentKey={placement.content_key}
      className={cn('empty:hidden', 'shrink-0')}
      style={placement.scrollable ? undefined : { minHeight }}
    >
      <CardErrorBoundary contentKey={placement.content_key}>
        <CardChromeProvider
          contentKey={placement.content_key}
          bundle={placement.typography ?? null}
          label={placement.label ?? null}
          iconName={placement.icon ?? null}
          strategy={placement.strategy ?? null}
        >
          <CardPlacementProvider
            value={{
              surface,
              slot_id: placement.slot_id,
              placement: { x, y, w, h },
            }}
          >
            <Component {...(placement.props ?? {})} />
          </CardPlacementProvider>
        </CardChromeProvider>
      </CardErrorBoundary>
    </CanvasCardShell>
  );
}

function PlacementSlot({ placement, cols, surface }: { placement: CardPlacement; cols: number; surface: CardSurface }) {
  const Component = CANVAS_CARD_REGISTRY[placement.content_key];

  if (!Component) {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        `[LayoutEngine] Unknown content_key '${placement.content_key}' — add to CANVAS_CARD_REGISTRY`,
      );
    }
    return null;
  }

  // Coordinates as data → CSS Grid placement. `x` is 0-based column
  // start (CSS Grid is 1-based, hence `+ 1`); `w` is column span;
  // `y` is 0-based row start; `h` is row span. `sticky` pins to
  // top of the scroll container.
  const w = Math.min(placement.w ?? 1, cols);
  const x = placement.x ?? 0;
  const y = placement.y ?? 0;
  const h = placement.h ?? 1;

  const style: React.CSSProperties = {
    gridColumnStart: x + 1,
    gridColumnEnd: `span ${w}`,
    gridRowStart: y + 1,
    gridRowEnd: `span ${h}`,
  };

  return (
    <CanvasCardShell
      contentKey={placement.content_key}
      className={cn(
        placement.sticky && 'sticky top-0 z-10',
        placement.scrollable && 'overflow-y-auto',
      )}
      style={style}
    >
      <CardErrorBoundary contentKey={placement.content_key}>
        <CardChromeProvider
          contentKey={placement.content_key}
          bundle={placement.typography ?? null}
          label={placement.label ?? null}
          iconName={placement.icon ?? null}
          strategy={placement.strategy ?? null}
        >
          <CardPlacementProvider
            value={{
              surface,
              slot_id: placement.slot_id,
              placement: { x, y, w, h },
            }}
          >
            <Component {...(placement.props ?? {})} />
          </CardPlacementProvider>
        </CardChromeProvider>
      </CardErrorBoundary>
    </CanvasCardShell>
  );
}
